import { Fragment } from "react";

import { Flex, Grid } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";

import { Button } from "ui/Button/Button";
import { WarningRoundIcon } from "ui/Icons/Icons";
import { Modal } from "ui/Modal/Modal";
import { BodyS, DetailM, HeaderS, TitleS } from "ui/Typography/Typography";

export type SubmissionSummaryLine = {
  name: string;
  received: number | null;
  returned: number;
};

export type SubmissionSummary = {
  TRTNumber: string;
  pickupDate: string;
  lines: SubmissionSummaryLine[];
  photoFileName: string | null;
  isPhotoMatchingTRTNumber: boolean;
};

type SubmissionSummaryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirmSubmit: () => void;
  onCancel: () => void;
  summary: SubmissionSummary;
};

const GRID_COLUMNS = "1fr 3rem 3rem";

function Row({
  label,
  children,
  isCritical = false,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  isCritical?: boolean;
}) {
  const color = isCritical ? "red.500" : undefined;
  return (
    <Flex justify="space-between" align="baseline" gap="s200" w="full">
      <BodyS color={color ?? "grey.700"}>{label}</BodyS>
      <TitleS textAlign="right" wordBreak="break-all" color={color}>
        {children}
      </TitleS>
    </Flex>
  );
}

function Warning({ children, ...rest }: { children: React.ReactNode; [key: string]: unknown }) {
  return (
    <Flex mt="s100" p="s150" gap="s100" bg="red.100" borderRadius="sm" align="flex-start" {...rest}>
      <WarningRoundIcon color="red.500" boxSize={6} flexShrink={0} />
      <BodyS color="red.500">{children}</BodyS>
    </Flex>
  );
}

function EquipmentTable({
  lines,
  isZeroReceived,
}: {
  lines: SubmissionSummaryLine[];
  isZeroReceived: boolean;
}) {
  const receivedColor = isZeroReceived ? "red.500" : undefined;

  return (
    <Grid
      templateColumns={GRID_COLUMNS}
      columnGap="s100"
      rowGap="s50"
      alignItems="baseline"
      w="full"
      data-testid="summary-equipment-table"
    >
      <DetailM />
      <DetailM textAlign="right" color={receivedColor ?? "grey.700"}>
        <FormattedMessage id="components.activities.transport-equipment.count-page.count.received" />
      </DetailM>
      <DetailM textAlign="right" color="grey.700">
        <FormattedMessage id="components.activities.transport-equipment.count-page.count.returned" />
      </DetailM>

      {lines.map(({ name, received, returned }) => (
        <Fragment key={name}>
          <BodyS color="grey.700">{name}</BodyS>
          <TitleS textAlign="right" color={receivedColor} data-testid="summary-received-cell">
            {/* An em dash, not a 0 - this type simply is not counted on the way in. */}
            {received ?? "—"}
          </TitleS>
          <TitleS textAlign="right" data-testid="summary-returned-cell">
            {returned}
          </TitleS>
        </Fragment>
      ))}
    </Grid>
  );
}

export function SubmissionSummaryModal({
  isOpen,
  onClose,
  onConfirmSubmit,
  onCancel,
  summary: { TRTNumber, pickupDate, lines, photoFileName, isPhotoMatchingTRTNumber },
}: SubmissionSummaryModalProps) {
  // Summing would count the em dashes as 0 and flag returned-only suppliers.
  const receivedLines = lines.filter(({ received }) => received !== null);
  const isZeroReceived =
    receivedLines.length > 0 && receivedLines.every(({ received }) => received === 0);
  const hasWarning = isZeroReceived || !isPhotoMatchingTRTNumber;

  const submitButton = (
    <Button
      onClick={onConfirmSubmit}
      variant={hasWarning ? "secondary" : "primary"}
      size="lg"
      isFullWidth
      data-testid="confirm-submission"
    >
      {isZeroReceived ? (
        <FormattedMessage id="components.activities.transport-equipment.count-page.zero-received-modal.cta.primary" />
      ) : (
        <FormattedMessage id="components.activities.transport-equipment.count-page.summary-modal.cta.primary" />
      )}
    </Button>
  );

  const goBackButton = (
    <Button
      onClick={onCancel}
      variant={hasWarning ? "primary" : "secondary"}
      size="lg"
      isFullWidth
      data-testid="cancel-submission"
    >
      <FormattedMessage id="components.activities.transport-equipment.count-page.summary-modal.cta.secondary" />
    </Button>
  );

  return (
    <Modal
      trapFocus={false}
      closeOnOverlayClick={false}
      isCentered
      scrollBehavior="inside"
      isOpen={isOpen}
      onClose={onClose}
    >
      <Modal.Content
        maxW="340px"
        w="calc(100% - 2rem)"
        maxH="calc(100% - 2rem)"
        pt="s300"
        px="s200"
        pb="s200"
      >
        <Modal.Header p="0">
          <HeaderS textAlign="center">
            <FormattedMessage id="components.activities.transport-equipment.count-page.summary-modal.title" />
          </HeaderS>
          <BodyS mt="s50" textAlign="center" color="grey.700" fontWeight="400">
            <FormattedMessage id="components.activities.transport-equipment.count-page.summary-modal.body" />
          </BodyS>
        </Modal.Header>
        <Modal.Body
          p="0"
          mt="s200"
          overflowY="auto"
          css={{
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Flex
            direction="column"
            gap="s100"
            p="s200"
            bg="grey.100"
            borderRadius="sm"
            data-testid="submission-summary"
          >
            <Row
              label={
                <FormattedMessage id="components.activities.transport-equipment.count-page.summary-modal.trt-label" />
              }
            >
              <span data-testid="summary-trt-number">{TRTNumber}</span>
            </Row>
            <Row
              label={
                <FormattedMessage id="components.activities.transport-equipment.count-page.summary-modal.date-label" />
              }
            >
              {pickupDate}
            </Row>
            <Row
              isCritical={!isPhotoMatchingTRTNumber}
              label={
                <FormattedMessage id="components.activities.transport-equipment.count-page.summary-modal.photo-label" />
              }
            >
              <span data-testid="summary-photo-filename">
                {photoFileName ?? (
                  <FormattedMessage id="components.activities.transport-equipment.count-page.summary-modal.photo-missing" />
                )}
              </span>
            </Row>
            <EquipmentTable lines={lines} isZeroReceived={isZeroReceived} />
          </Flex>

          {isZeroReceived && (
            <Warning data-testid="summary-zero-received">
              <FormattedMessage id="components.activities.transport-equipment.count-page.zero-received-modal.body" />
            </Warning>
          )}

          {!isPhotoMatchingTRTNumber && (
            <Warning data-testid="summary-photo-mismatch">
              <FormattedMessage id="components.activities.transport-equipment.count-page.summary-modal.photo-mismatch" />
            </Warning>
          )}
        </Modal.Body>
        <Modal.Footer p="0" mt="s200" flexDir="column" gap="s100">
          {hasWarning ? (
            <>
              {goBackButton}
              {submitButton}
            </>
          ) : (
            <>
              {submitButton}
              {goBackButton}
            </>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
